// "Eval cases" tab of the Agent Eval page: the eval scenarios the reflecting
// agent proposed (GET /cases), each with its TestCase payload and the
// Accept / Reject / Reopen moves of the curation FSM (POST /cases/{id}/curate;
// a 409 from the backend names the illegal move and is shown as is).

import React, { useCallback, useEffect, useState } from 'react';

import {
  CASE_TRANSITIONS,
  feedbackLearningApi,
  type FeedbackCase,
  type FeedbackCaseStatus,
} from '../../services/feedbackLearning';
import styles from '../AgentEvalPage.module.css';
import { errorMessage, formatTs, pretty } from './format';

type StatusFilter = FeedbackCaseStatus | 'any';

const MOVE_LABEL: Record<FeedbackCaseStatus, string> = {
  accepted: 'Accept',
  rejected: 'Reject',
  proposed: 'Reopen',
};

function caseStatusClass(status: FeedbackCaseStatus): string {
  if (status === 'accepted') return `${styles.tag} ${styles.ok}`;
  if (status === 'rejected') return `${styles.tag} ${styles.flagged}`;
  return `${styles.tag} ${styles.pending}`;
}

function CaseRow({
  row,
  expanded,
  onToggle,
  onCurated,
}: {
  row: FeedbackCase;
  expanded: boolean;
  onToggle: () => void;
  onCurated: (next: FeedbackCase) => void;
}): React.ReactElement {
  const [notes, setNotes] = useState<string>(row.curation_notes ?? '');
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setNotes(row.curation_notes ?? '');
  }, [row.id, row.curation_notes]);

  const curate = useCallback(
    async (target: FeedbackCaseStatus) => {
      setSaving(true);
      setError(null);
      try {
        const next = await feedbackLearningApi.curateCase(row.id, target, notes || undefined);
        onCurated(next);
      } catch (e) {
        setError(errorMessage(e, 'failed to curate the case'));
      } finally {
        setSaving(false);
      }
    },
    [row.id, notes, onCurated],
  );

  return (
    <>
      <tr
        className={styles.clickable}
        onClick={onToggle}
        data-action="agent_eval_case_toggle"
        data-target-case-id={row.id}
        data-outcome={expanded ? 'close' : 'open'}
        aria-expanded={expanded}
      >
        <td>{formatTs(row.created_at)}</td>
        <td>{row.agent_type}</td>
        <td>{row.case_id}</td>
        <td>{row.intent_family}</td>
        <td>
          <span className={caseStatusClass(row.status)}>{row.status}</span>
        </td>
        <td>{formatTs(row.curated_at)}</td>
      </tr>
      {expanded && (
        <tr className={styles['case-detail-row']}>
          <td colSpan={6}>
            <div className={styles['case-detail']} data-testid={`case-detail-${row.case_id}`}>
              <h3>TestCase payload</h3>
              <pre className={styles['audit-pre']}>{pretty(row.case)}</pre>
              <div className={styles['triage-card']}>
                <h3>Curation</h3>
                <div>
                  Currently <b>{row.status}</b>
                  {row.curated_at && <> (curated {formatTs(row.curated_at)})</>}
                  <span className={styles.sub}> · proposed by lesson {row.lesson_id}</span>
                </div>
                <div className={styles.actions}>
                  {CASE_TRANSITIONS[row.status].map((target) => (
                    <button
                      key={target}
                      type="button"
                      disabled={saving}
                      className={target === 'rejected' ? styles.clear : ''}
                      onClick={(e) => {
                        e.stopPropagation();
                        void curate(target);
                      }}
                      data-action={`agent_eval_case_${target === 'proposed' ? 'reopen' : target === 'accepted' ? 'accept' : 'reject'}`}
                      data-target-case-id={row.id}
                      data-outcome="request"
                    >
                      {MOVE_LABEL[target]}
                    </button>
                  ))}
                </div>
                <div className={styles['notes-row']}>
                  <textarea
                    placeholder="Curation notes (optional)…"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    disabled={saving}
                    aria-label={`Curation notes for ${row.case_id}`}
                  />
                </div>
                {error && (
                  <div className={styles.error} data-testid={`case-error-${row.case_id}`}>
                    {error}
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

export function FeedbackCasesTab({
  onCurated,
}: {
  /** Fired after a successful curation so the stats strip can refresh. */
  onCurated?: () => void;
}): React.ReactElement {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('proposed');
  const [agentTypeFilter, setAgentTypeFilter] = useState<string>('');
  const [rows, setRows] = useState<FeedbackCase[]>([]);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await feedbackLearningApi.listCases({
        status: statusFilter === 'any' ? undefined : statusFilter,
        agent_type: agentTypeFilter.trim() || undefined,
        limit: 200,
      });
      setRows(data);
    } catch (e) {
      setError(errorMessage(e, 'failed to load eval cases'));
    } finally {
      setLoading(false);
    }
  }, [statusFilter, agentTypeFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleCurated = useCallback(
    (next: FeedbackCase) => {
      // Keep the row in place (with its new status) so the reviewer sees the
      // result; it drops out on the next filter change or reload.
      setRows((prev) => prev.map((r) => (r.id === next.id ? next : r)));
      onCurated?.();
    },
    [onCurated],
  );

  return (
    <div data-section="feedback_cases">
      <div className={styles.filters}>
        <label htmlFor="case-status">Status</label>
        <select
          id="case-status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          data-action="agent_eval_cases_status_filter"
          data-record-value="status"
        >
          <option value="proposed">Proposed (needs a verdict)</option>
          <option value="accepted">Accepted</option>
          <option value="rejected">Rejected</option>
          <option value="any">Any</option>
        </select>
        <label htmlFor="case-agent">Agent</label>
        <input
          id="case-agent"
          placeholder="(any)"
          value={agentTypeFilter}
          onChange={(e) => setAgentTypeFilter(e.target.value)}
          data-action="agent_eval_cases_agent_filter"
          data-record-value="agent_type"
        />
      </div>

      {error && <div className={styles.error}>{error}</div>}

      <table className={styles.list} aria-label="Proposed eval cases">
        <thead>
          <tr>
            <th>Proposed</th>
            <th>Agent</th>
            <th>Case</th>
            <th>Intent family</th>
            <th>Status</th>
            <th>Curated</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <CaseRow
              key={r.id}
              row={r}
              expanded={expandedId === r.id}
              onToggle={() => setExpandedId((cur) => (cur === r.id ? null : r.id))}
              onCurated={handleCurated}
            />
          ))}
          {!loading && rows.length === 0 && (
            <tr>
              <td colSpan={6} className={styles.empty}>
                {statusFilter === 'proposed'
                  ? 'queue is clear — no proposed cases waiting for a verdict'
                  : 'no cases match these filters'}
              </td>
            </tr>
          )}
          {loading && (
            <tr>
              <td colSpan={6} className={styles.empty}>
                loading…
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
